import { connect, useDispatch } from "react-redux";
import { KeyboardEvent, useEffect, useState } from "react";
import Select from 'react-select';
import { Button, Badge } from "react-bootstrap";
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';

import { RootState } from "@/store";
import apiService from "@/api-service";
import { HoofdcodesState } from "@/store/reducers/hoofdcodes";
import { OvksState, ovksActions } from "@/store/reducers/ovks";
import { OvereenkomstHouder } from "@/models/ovk";
import { BestekFilterState, BestekFilterOptiesResponseData, bestekFilterActions } from "@/store/reducers/bestek_filter"; 
import FloatingDatepicker from "../floating-datepicker/floating-datepicker";
import HoofdcodeWerkcatFilter from "../hoofdcode_filters/werkcategorie_filter";
import HoofdcodeSubwerkcatFilter from "../hoofdcode_filters/subwerkcategorie_filter";
import HoofdcodeFilter from "../hoofdcode_filters/hoofdcode_filter";
import { BestekFilterParams, initialBestekFilterParams, ZOEK_CATEGORIEEN, ZoekCategorie } from "./bestek-filter-params";
import FilterInfoButton from "../common/filter-info-button";

interface OwnProps {
    disableHoofdCodeFiltering?: 'partial' | 'full';
    result_count?: number;
    stateContext?: 'global' | 'local';
    initialLocalParams?: BestekFilterParams;
    onFilterButtonClick: (params?: BestekFilterParams) => void;
}

interface Props extends OwnProps {
    hoofdcodes: HoofdcodesState,
    ovks: OvksState,
    opties: BestekFilterOptiesResponseData | null,
    filter_values: BestekFilterState,
    result_count: number,
    filterVisible: boolean
}

function mapStateToProps(state: RootState, ownProps: OwnProps): Props {
    return Object.assign({
        hoofdcodes: state.hoofdcodes,
        ovks: state.ovks,
        opties: state.bestek_filter.opties,
        filter_values: state.bestek_filter,
        result_count: ownProps.result_count ?? (state.bestek_filter.result?.length ?? 0),
        filterVisible: state.bestek_filter.filterVisible
    }, ownProps);
}

const targetHeight = 30;

export const selectStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: 'initial',
    fontSize: '15px'
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: '0 3px',
  }),
  clearIndicator: (base: any) => ({
    ...base,
    padding: `${(targetHeight - 20 - 1 - 1) / 2}px`,
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    padding: `${(targetHeight - 20 - 1 - 1) / 2}px`,
  })
};

export const selectStylesNoWrap = {
  ...selectStyles,
  valueContainer: (base: any) => ({
    ...base,
    padding: '0 3px',
    flexWrap: 'nowrap' as const,
    overflow: 'hidden',
  }),
};

const BestekFilter = (props: Props) => {
    const context: 'global' | 'local' = props.stateContext ?? 'global';
    const disableHoofdCodeFiltering: 'none' | 'partial' | 'full' = props.disableHoofdCodeFiltering ?? 'none';
    const dispatch = useDispatch();

    // NIEUW: Client-side hydration check
    const [isClient, setIsClient] = useState(false);
    const [clientFilterVisible, setClientFilterVisible] = useState(true); // Default voor SSR

    // Multi-term search state
    const [searchTerms, setSearchTerms] = useState<string[]>([]);
    const [currentSearchInput, setCurrentSearchInput] = useState('');
    const [pendingSearch, setPendingSearch] = useState(false); // NEW: Flag for pending auto-add search

    const [ localFilterValues, setLocalFilterValues ] = useState<BestekFilterParams>(props.initialLocalParams ?? initialBestekFilterParams);
    const filterValues: BestekFilterParams | BestekFilterState = context === 'global' ? props.filter_values : localFilterValues;

    // NIEUW: "Release versie" (Bestek.versie) cascadeert onder Jaarversie — apart, geparametriseerd
    // endpoint, dus niet onderdeel van de statische bulk-opties (props.opties).
    const [releaseOpties, setReleaseOpties] = useState<string[]>([]);
    useEffect(() => {
        const params = filterValues.jaarversie ? `?versie_jaarversie=${encodeURIComponent(filterValues.jaarversie)}` : '';
        apiService.get<string[]>(`/bestek/releases${params}`)
            .then(r => setReleaseOpties(r.data))
            .catch(err => console.error('Error loading releases:', err));
    }, [filterValues.jaarversie]);

    // NEW: Effect to trigger search after auto-add Redux state update
    useEffect(() => {
        if (pendingSearch && context === 'global') {
            console.log('Triggering delayed search after auto-add Redux update');
            setPendingSearch(false);
            props.onFilterButtonClick();
        }
    }, [props.filter_values.beschrijving, pendingSearch]);

    // Client-side hydration effect
    useEffect(() => {
        setIsClient(true);
        setClientFilterVisible(props.filterVisible);
        console.log('BestekFilter mounted, filterVisible from props:', props.filterVisible);
    }, []);

    // Update client state when props change (na hydration)
    useEffect(() => {
        if (isClient) {
            setClientFilterVisible(props.filterVisible);
            console.log('Props filterVisible changed to:', props.filterVisible);
        }
    }, [props.filterVisible, isClient]);

    // Toggle filter visibility functie met debug logging
    const toggleFilterVisibility = () => {
        console.log('Toggle button clicked, current visibility:', getFilterVisibility());
        dispatch(bestekFilterActions.toggleFilterVisibility());
    };

    // Safe filter visibility getter
    const getFilterVisibility = (): boolean => {
        if (!isClient) {
            // Tijdens SSR, gebruik een safe default
            return true;
        }
        return clientFilterVisible;
    };

    // Fetch data from API
    async function fetch() {
        try {
            let [ovks, filter_opties] = await Promise.all([
                props.ovks.loaded ? Promise.resolve(props.ovks.data) : apiService.get<OvereenkomstHouder[]>("/ovk/").then(r => r.data),
                props.opties !== null ? Promise.resolve(props.opties) : apiService.get<BestekFilterOptiesResponseData>('/bestek/filter-opties').then(r => r.data)
            ]);
    
            dispatch(ovksActions.set_data(ovks));
            dispatch(bestekFilterActions.setFilterOpties(filter_opties));
        } catch (e) {
            console.error(e);
        }
    }

    useEffect(() => {
        fetch();
    }, []);

    // Sync searchTerms with filter values on component mount/update
    useEffect(() => {
        const currentBeschrijving = Array.isArray(filterValues.beschrijving) ? filterValues.beschrijving : 
                                  (filterValues.beschrijving ? [filterValues.beschrijving] : []);
        
        if (JSON.stringify(currentBeschrijving) !== JSON.stringify(searchTerms)) {
            setSearchTerms(currentBeschrijving);
        }
    }, [filterValues.beschrijving]);

    // NIEUWE: Verbeterde addSearchTerm functie
    const addSearchTerm = (term: string) => {
        const trimmedInput = term.trim();
        if (trimmedInput && !searchTerms.includes(trimmedInput)) {
            const newTerms = [...searchTerms, trimmedInput];
            setSearchTerms(newTerms);
            setCurrentSearchInput('');
            
            if (context === 'global') {
                dispatch(bestekFilterActions.setValue(['beschrijving', newTerms]));
            } else {
                const updatedParams = { ...localFilterValues, beschrijving: newTerms };
                setLocalFilterValues(updatedParams);
            }
        }
    };

    // VERBETERDE: Multi-term search handlers
    const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addSearchTerm(currentSearchInput);
        } else if (e.key === 'Backspace' && currentSearchInput === '' && searchTerms.length > 0) {
            const newTerms = searchTerms.slice(0, -1);
            setSearchTerms(newTerms);
            
            if (context === 'global') {
                dispatch(bestekFilterActions.setValue(['beschrijving', newTerms]));
            } else {
                const updatedParams = { ...localFilterValues, beschrijving: newTerms };
                setLocalFilterValues(updatedParams);
            }
        }
    };

    const removeSearchTerm = (indexToRemove: number) => {
        const newTerms = searchTerms.filter((_, index) => index !== indexToRemove);
        setSearchTerms(newTerms);
        
        if (context === 'global') {
            dispatch(bestekFilterActions.setValue(['beschrijving', newTerms]));
        } else {
            const updatedParams = { ...localFilterValues, beschrijving: newTerms };
            setLocalFilterValues(updatedParams);
        }
    };

    const clearAllSearchTerms = () => {
        setSearchTerms([]);
        setCurrentSearchInput('');
        
        if (context === 'global') {
            dispatch(bestekFilterActions.setValue(['beschrijving', []]));
        } else {
            const updatedParams = { ...localFilterValues, beschrijving: [] };
            setLocalFilterValues(updatedParams);
        }
    };

    const handleKeyDownEvent = (e: KeyboardEvent) => {
        if (e.code === 'Enter') {
            if (getFilterVisibility()) {
                handleFilterButtonClick();
            }
        }
    }

    const onResetButtonClick = (): void => {
        if (context === 'global') {
            dispatch(bestekFilterActions.reset());
            setSearchTerms([]);
            setCurrentSearchInput('');

            setTimeout(() => {
                handleFilterButtonClick();
            }, 250);
        } else {
            setLocalFilterValues(props.initialLocalParams ?? initialBestekFilterParams);
            setSearchTerms([]);
            setCurrentSearchInput('');
            props.onFilterButtonClick(props.initialLocalParams ?? initialBestekFilterParams);
        }
    }

    // NIEUWE: Hybride handleFilterButtonClick met auto-add functionaliteit
    const handleFilterButtonClick = (): void => {
        // AUTO-ADD: Voeg onvoltooide zoekterm automatisch toe bij Toepassen
        if (currentSearchInput.trim() && !searchTerms.includes(currentSearchInput.trim())) {
            const trimmedInput = currentSearchInput.trim();
            const newTerms = [...searchTerms, trimmedInput];
            
            // Update local states direct
            setSearchTerms(newTerms);
            setCurrentSearchInput('');
            
            if (context === 'global') {
                // Update Redux state en zet pending flag
                dispatch(bestekFilterActions.setValue(['beschrijving', newTerms]));
                setPendingSearch(true); // This will trigger useEffect when Redux state updates
                
            } else {
                // Voor local context is dit eenvoudiger - direct uitvoeren
                const updatedParams = { ...localFilterValues, beschrijving: newTerms };
                setLocalFilterValues(updatedParams);
                props.onFilterButtonClick(updatedParams);
            }
        } else {
            // Normale flow zonder onvoltooide input
            if (context === 'global') {
                props.onFilterButtonClick();
            } else {
                props.onFilterButtonClick({ ...localFilterValues });
            }
        }
    }

    const handleValueChange = (key: keyof BestekFilterParams, value: any): void => {
        if (context === 'global') {
            dispatch(bestekFilterActions.setValue([key, value]));
        } else {
            const newParams = { ...localFilterValues };
            newParams[key] = value;
            setLocalFilterValues(newParams);
        }
    } 

    // Debug render info
    console.log('BestekFilter render - isClient:', isClient, 'filterVisible:', getFilterVisibility());

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0 d-flex align-items-center gap-1">
                    Zoekfilters
                    <FilterInfoButton
                        id="bestek-filter-uitleg"
                        text="Voeg zoektermen toe via Enter. Alle termen moeten aanwezig zijn in het bestek. Omsluit een woord met spaties om het alleen als zelfstandige term te vinden. Locatie, categorie en hoofdcodefilters werken als trechter: elke keuze verfijnt de volgende filter."
                    />
                </h5>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={toggleFilterVisibility}
                >
                    {getFilterVisibility() ? 'Filter verbergen' : 'Filter tonen'}
                </Button>
            </div>

            {/* Gebruik de hydration-safe visibility check */}
            {getFilterVisibility() && (
                <Container fluid>
                    <Row className="mt-2" style={{rowGap: '5px'}}>
                        <Col xs={12} md={12} lg={6}>
                            <label htmlFor="beschrijving-input">Registratienummer/zoektekst</label>
                            
                            {/* VERBETERDE: Multi-term search input met visuele feedback */}
                            <div className="d-flex flex-wrap align-items-center gap-1 p-2 border rounded" 
                                 style={{ 
                                     minHeight: '38px', 
                                     backgroundColor: 'white',
                                     borderColor: currentSearchInput.trim() ? '#0d6efd' : '#dee2e6',
                                     borderWidth: currentSearchInput.trim() ? '2px' : '1px',
                                     transition: 'border-color 0.2s, border-width 0.2s'
                                 }}>
                                
                                {/* Toon alle zoektermen als badges */}
                                {searchTerms.map((term, index) => (
                                    <Badge 
                                        key={index} 
                                        bg="primary" 
                                        className="d-flex align-items-center gap-1"
                                        style={{ fontSize: '0.875rem' }}
                                    >
                                        {term}
                                        <button
                                            type="button"
                                            className="btn-close btn-close-white"
                                            style={{ fontSize: '0.65rem', width: '0.7em', height: '0.7em' }}
                                            onClick={() => removeSearchTerm(index)}
                                            aria-label={`Verwijder zoekterm ${term}`}
                                        />
                                    </Badge>
                                ))}
                                
                                {/* Input veld voor nieuwe term */}
                                <Form.Control
                                    id="beschrijving-input"
                                    value={currentSearchInput}
                                    onChange={(e) => setCurrentSearchInput(e.target.value)}
                                    onKeyDown={handleSearchKeyDown}
                                    placeholder={searchTerms.length === 0 ? "Typ zoekterm en druk Enter..." : "Voeg nog een term toe..."}
                                    style={{ 
                                        border: 'none', 
                                        outline: 'none', 
                                        boxShadow: 'none',
                                        flex: 1,
                                        minWidth: '120px'
                                    }}
                                />
                                
                                {/* NIEUWE: Visuele Add button voor duidelijkheid */}
                                {currentSearchInput.trim() && (
                                    <Button 
                                        size="sm" 
                                        variant="outline-primary"
                                        onClick={() => addSearchTerm(currentSearchInput)}
                                        style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                                        title="Voeg zoekterm toe"
                                    >
                                        +
                                    </Button>
                                )}
                            </div>
                            
                            {/* VERBETERDE: Slimme hulptekst met contextafhankelijke feedback */}
                            <div className="d-flex justify-content-between align-items-center mt-1">
                                <small className="text-muted">
                                    {currentSearchInput.trim() ? (
                                        <span style={{ color: '#0d6efd', fontWeight: 500 }}>
                                            Druk Enter, klik + of "Toepassen" om toe te voegen
                                        </span>
                                    ) : searchTerms.length === 0 ? null : (
                                        `${searchTerms.length} zoekterm${searchTerms.length !== 1 ? 'en' : ''} • Voeg meer toe of druk "Toepassen"`
                                    )}
                                </small>
                                {searchTerms.length > 0 && (
                                    <Button 
                                        variant="link" 
                                        size="sm" 
                                        className="p-0 text-muted"
                                        onClick={clearAllSearchTerms}
                                        style={{ fontSize: '0.75rem' }}
                                    >
                                        Wis alle termen
                                    </Button>
                                )}
                            </div>
                        </Col>
                        
                        <Col xs={12} md={12} lg={6}>
                            <label htmlFor="zoek-categorien-select">Zoeken in</label>
                            <Select 
                                styles={selectStyles}
                                inputId="zoek-categorien-select"
                                options={ZOEK_CATEGORIEEN}
                                value={filterValues.zoek_categorien || [ZOEK_CATEGORIEEN[0]]}
                                getOptionLabel={(o) => o.label}
                                getOptionValue={(o) => o.value}
                                onChange={(selectedOptions) => {
                                    const categories = selectedOptions && selectedOptions.length > 0 
                                        ? selectedOptions 
                                        : [ZOEK_CATEGORIEEN[0]];
                                    handleValueChange('zoek_categorien', categories);
                                }}
                                isMulti
                                closeMenuOnSelect={false}
                                placeholder="Selecteer zoek categorieën..."
                            />
                        </Col>
                        
                        <Col xs={12} md={6} lg={4}>
                            <label htmlFor="ovks-select-box">Overeenkomsthouders</label>
                            <Select 
                                styles={selectStyles}
                                inputId="ovks-select-box" 
                                options={props.ovks.data} 
                                value={filterValues.ovks}
                                getOptionLabel={(o) => `${o.ovk_nummer} - ${o.ovk_naam}`} 
                                getOptionValue={(o) => o.id.toString()} 
                                onChange={(v) => handleValueChange('ovks', v.map(v => v)) }
                                isLoading={!props.ovks.loaded} 
                                isMulti isClearable />
                        </Col>

                        {
                            (disableHoofdCodeFiltering === 'none' || disableHoofdCodeFiltering === 'partial') && (
                                <>
                                    <Col xs={12} md={6} lg={props.disableHoofdCodeFiltering === 'partial' ? 6 : 4}>
                                        <HoofdcodeWerkcatFilter onChange={(v) => handleValueChange("hoofdcodes_werkcats", v)} selected={filterValues.hoofdcodes_werkcats} />
                                    </Col>

                                    <Col xs={12} md={6} lg={props.disableHoofdCodeFiltering === 'partial' ? 6 : 4}>
                                        <HoofdcodeSubwerkcatFilter onChange={(v) => handleValueChange("hoofdcodes_subwerkcats", v)} selected={filterValues.hoofdcodes_subwerkcats} werkcats={filterValues.hoofdcodes_werkcats} />
                                    </Col>

                                    {
                                        disableHoofdCodeFiltering === 'none' && (
                                            <Col xs={12} md={6} lg={4}>
                                                <HoofdcodeFilter 
                                                    werkcats={filterValues.hoofdcodes_werkcats}
                                                    subwerkcats={filterValues.hoofdcodes_subwerkcats}
                                                    onChange={(v => handleValueChange('hoofdcodes', v))} 
                                                    selected={filterValues.hoofdcodes} />
                                            </Col>
                                        )
                                    }
                                </>
                            )
                        }

                        <Col xs={12} md={4}>
                            <label htmlFor="provincie-select-box">Provincie</label>
                            <Select 
                                styles={selectStyles}
                                inputId="provincie-select-box" 
                                options={props.opties ? props.opties.provincies.map(o => { return { label: o, value: o } }) : []} 
                                value={filterValues.provincie ? { label: filterValues.provincie, value: filterValues.provincie } : null}
                                getOptionLabel={(o) => o.label} 
                                getOptionValue={(o) => o.value} 
                                onChange={(v) => handleValueChange('provincie', v?.value ?? "")}
                                isLoading={!props.opties}
                                isClearable />
                        </Col>

                        <Col xs={12} md={4}>
                            <label htmlFor="gemeente-select-box">Gemeente</label>
                            <Select 
                                styles={selectStyles}
                                inputId="gemeente-select-box" 
                                options={props.opties ? props.opties.gemeentes.map(o => { return { label: o, value: o } }) : []} 
                                value={filterValues.gemeente ? { label: filterValues.gemeente, value: filterValues.gemeente } : null}
                                getOptionLabel={(o) => o.label} 
                                getOptionValue={(o) => o.value} 
                                onChange={(v) => handleValueChange('gemeente', v?.value ?? "")}
                                isLoading={!props.opties} 
                                isClearable />
                        </Col>

                        <Col xs={12} md={4}>
                            <label htmlFor="woonplaats-select-box">Stad/dorp</label>
                            <Select 
                                styles={selectStyles}
                                inputId="woonplaats-select-box" 
                                options={props.opties ? props.opties.woonplaatsen.map(o => { return { label: o, value: o } }) : []} 
                                value={filterValues.woonplaats ? { label: filterValues.woonplaats, value: filterValues.woonplaats } : null}
                                getOptionLabel={(o) => o.label} 
                                getOptionValue={(o) => o.value} 
                                onChange={(v) => handleValueChange('woonplaats', v?.value ?? "")}
                                isLoading={!props.opties}
                                isClearable />
                        </Col>

                        <Col xs={12} md={4}>
                            <label htmlFor="jaarversie-select-box">Jaarversie</label>
                            <Select
                                styles={selectStyles}
                                inputId="jaarversie-select-box"
                                options={props.opties ? props.opties.jaarversies.map(o => { return { label: o, value: o } }) : []}
                                value={filterValues.jaarversie ? { label: filterValues.jaarversie, value: filterValues.jaarversie } : null}
                                getOptionLabel={(o) => o.label}
                                getOptionValue={(o) => o.value}
                                onChange={(v) => {
                                    handleValueChange('jaarversie', v?.value ?? "");
                                    // Release cascadeert onder jaarversie; bij wisselen van
                                    // jaarversie kan de eerder gekozen release niet meer geldig
                                    // zijn, dus resetten.
                                    handleValueChange('release', "");
                                }}
                                isLoading={!props.opties}
                                isClearable />
                        </Col>

                        <Col xs={12} md={4}>
                            <label htmlFor="release-select-box">Release versie</label>
                            <Select
                                styles={selectStyles}
                                inputId="release-select-box"
                                options={releaseOpties.map(o => { return { label: o, value: o } })}
                                value={filterValues.release ? { label: filterValues.release, value: filterValues.release } : null}
                                getOptionLabel={(o) => o.label}
                                getOptionValue={(o) => o.value}
                                onChange={(v) => handleValueChange('release', v?.value ?? "")}
                                isClearable />
                        </Col>

                        <Col xs={12} md={4}>
                            <label>Wijzigingsdatum</label>
                            <div className="d-flex" style={{columnGap: '5px'}}>
                                <FloatingDatepicker placeholder="Vanaf..." date={filterValues.wijzigings_datum_vanaf} maxDate={filterValues.wijzigings_datum_tm} dateChange={(d) => handleValueChange('wijzigings_datum_vanaf', d)} />
                                <FloatingDatepicker placeholder="Tot en met..." date={filterValues.wijzigings_datum_tm} minDate={filterValues.wijzigings_datum_vanaf} dateChange={(d) => handleValueChange('wijzigings_datum_tm', d)} />
                            </div>
                        </Col>

                        <Col xs={12} md={4}>
                            <label>Aanmaakdatum</label>
                            <div className="d-flex" style={{columnGap: '5px'}}>
                                <FloatingDatepicker placeholder="Vanaf..." date={filterValues.aanmaak_datum_vanaf} maxDate={filterValues.aanmaak_datum_tm} dateChange={(d) => handleValueChange('aanmaak_datum_vanaf', d)} />
                                <FloatingDatepicker placeholder="Tot en met..." date={filterValues.aanmaak_datum_tm} minDate={filterValues.aanmaak_datum_vanaf} dateChange={(d) => handleValueChange('aanmaak_datum_tm', d)} />
                            </div>
                        </Col>

                    </Row>

                    <Row className="mt-3">
                        <Col>
                            <Button onClick={handleFilterButtonClick} variant="primary" style={{float: 'right'}}>Toepassen</Button>
                            <Button onClick={onResetButtonClick} variant="light" style={{float: 'right', marginRight: '5px'}}>Reset filter</Button>
                        </Col>
                    </Row>
                </Container>
            )}
        </>
    )
}

export default connect(mapStateToProps)(BestekFilter);